import React from "react";
import { IRouter } from "@interfaces";
import { Outlet } from "react-router";
import { EmptyMessage } from "@nubeteck/prime";
import { AppRoutes, AppRouteNames } from "@core";

import { Permissions } from "./core";
import { AccountsPayableRoutes, AccountsPayableRouteNames } from "./routes";
import {
  ProviderPage,
  ProviderFormPage,
  DocumentUploadPage,
  ProviderDetailPage,
  AccountsPayablePage,
  PaymentRequestsPage,
  DeliveryNoteEditPage,
  PurchaseDocumentsPage,
  PurchaseOrderEditPage,
  DeliveryNoteDetailPage,
  PurchaseInvoiceEditPage,
  PurchaseOrderDetailPage,
  PurchaseInvoiceDetailPage,
  RecurringPurchaseInvoicesPage,
} from "./pages";

// To avoid first feature loading...
const ReportingLayout = React.lazy(() =>
  import("@components").then((a) => ({ default: a.ReportingLayout })),
);
const ReportingPage = React.lazy(() =>
  import("@pages").then((a) => ({ default: a.ReportingPage })),
);

const router: IRouter = {
  sidebar: true,
  element: <Outlet />,
  iconName: "receipt",
  title: "Cuentas por pagar",
  id: "feature-accounts-payable-root",
  permissions: [Permissions.SEE_ACCOUNTS_PAYABLE_MODULE],
  path: AccountsPayableRoutes[
    AccountsPayableRouteNames.DASHBOARD_PAGE
  ].template(),
  children: [
    {
      path: "",
      index: true,
      sidebar: true,
      dashboardId: 2065,
      iconName: "dashboard",
      title: "Panel de control",
      id: "feature-accounts-payable-dashboard-page",
    },
    {
      sidebar: true,
      iconName: "receipt",
      title: "Cuentas por pagar",
      element: <AccountsPayablePage />,
      id: "feature-accounts-payable-page",
      path: AccountsPayableRoutes[
        AccountsPayableRouteNames.ACCOUNTS_PAYABLE
      ].template(),
    },
    {
      sidebar: true,
      iconName: "receipt",
      title: "Solicitudes pagos",
      element: <PaymentRequestsPage />,
      id: "feature-accounts-payable-payment-requests-page",
      path: AccountsPayableRoutes[
        AccountsPayableRouteNames.PAYMENT_REQUESTS
      ].template(),
    },
    {
      sidebar: true,
      element: <Outlet />,
      iconName: "shopping-cart",
      title: "Documentos de compra",
      id: "feature-accounts-payable-purchase-documents-root",
      path: AccountsPayableRoutes[
        AccountsPayableRouteNames.PURCHASE_DOCUMENTS
      ].template(),
      children: [
        {
          path: "",
          title: "",
          sidebar: false,
          element: <PurchaseDocumentsPage />,
          id: "feature-accounts-payable-purchase-documents-page",
        },
        {
          sidebar: false,
          title: "Facturas de compras recurrentes",
          element: <RecurringPurchaseInvoicesPage />,
          id: "feature-accounts-payable-recurring-purchase-invoices-page",
          path: AccountsPayableRoutes[
            AccountsPayableRouteNames.RECURRING
          ].template(),
        },
        {
          sidebar: false,
          title: "Factura de compra",
          element: <PurchaseInvoiceEditPage />,
          id: "feature-accounts-payable-purchase-invoice-form-page",
          path: AccountsPayableRoutes[
            AccountsPayableRouteNames.PURCHASE_INVOICE
          ].template(),
        },
        {
          sidebar: false,
          title: "Orden de compra",
          element: <PurchaseOrderEditPage />,
          id: "feature-accounts-payable-purchase-order-form-page",
          path: AccountsPayableRoutes[
            AccountsPayableRouteNames.PURCHASE_ORDER
          ].template(),
        },
        {
          sidebar: false,
          title: "Conduce",
          element: <DeliveryNoteEditPage />,
          id: "feature-accounts-payable-purchase-delivery-note-form-page",
          path: AccountsPayableRoutes[
            AccountsPayableRouteNames.PURCHASE_DELIVERY_NOTE
          ].template(),
        },
        {
          sidebar: false,
          title: "Detalle factura de compra",
          element: <PurchaseInvoiceDetailPage />,
          id: "feature-accounts-payable-purchase-invoice-detail-page",
          path: AccountsPayableRoutes[
            AccountsPayableRouteNames.PURCHASE_INVOICE_DETAIL
          ].template(),
        },
        {
          sidebar: false,
          title: "Detalle orden de compra",
          element: <PurchaseOrderDetailPage />,
          id: "feature-accounts-payable-purchase-order-detail-page",
          path: AccountsPayableRoutes[
            AccountsPayableRouteNames.PURCHASE_ORDER_DETAIL
          ].template(),
        },
        {
          sidebar: false,
          title: "Detalle conduce",
          element: <DeliveryNoteDetailPage />,
          id: "feature-accounts-payable-purchase-delivery-note-detail-page",
          path: AccountsPayableRoutes[
            AccountsPayableRouteNames.PURCHASE_DELIVERY_NOTE_DETAIL
          ].template(),
        },
      ],
    },
    {
      sidebar: true,
      iconName: "file-upload",
      title: "Carga de documentos",
      element: <DocumentUploadPage />,
      id: "feature-accounts-payable-document-upload-page",
      path: AccountsPayableRoutes[
        AccountsPayableRouteNames.DOCUMENT_UPLOAD
      ].template(),
    },
    {
      sidebar: true,
      iconName: "package",
      element: <Outlet />,
      title: "Proveedores",
      id: "feature-accounts-payable-providers-root",
      path: AccountsPayableRoutes[
        AccountsPayableRouteNames.PROVIDER_PAGE
      ].template(),
      children: [
        {
          path: "",
          index: true,
          sidebar: false,
          title: "Proveedores",
          element: <ProviderPage />,
          id: "feature-accounts-payable-providers-page",
        },
        {
          sidebar: false,
          title: "{name}",
          element: <ProviderDetailPage />,
          id: "feature-accounts-payable-provider-details-page",
          path: AccountsPayableRoutes[
            AccountsPayableRouteNames.PROVIDER_DETAILS_PAGE
          ].template(),
        },
        {
          sidebar: false,
          title: "{title}",
          element: <ProviderFormPage />,
          id: "feature-accounts-payable-provider-form-page",
          path: AccountsPayableRoutes[
            AccountsPayableRouteNames.PROVIDER_FORM_PAGE
          ].template(),
        },
      ],
    },
    {
      sidebar: true,
      title: "Reportes",
      iconName: "report",
      element: <ReportingLayout moduleId={9} />,
      id: "feature-accounts-payable-reports-root",
      path: AppRoutes[AppRouteNames.REPORTING_PAGE].template(),
      permissions: [Permissions.SEE_ACCOUNTS_PAYABLE_REPORTING_PAGE],
      children: [
        {
          path: "",
          sidebar: false,
          title: "Selecciona un reporte",
          id: "feature-accounts-payable-reports-page",
          element: (
            <EmptyMessage
              customWidth="300px"
              message="Selecciona un reporte para generar"
            />
          ),
        },
        {
          sidebar: false,
          title: "{reportName}",
          element: <ReportingPage />,
          id: "feature-accounts-payable-reports-form-page",
          path: AppRoutes[AppRouteNames.REPORTING_PAGE_ID].template(),
        },
      ],
    },
  ],
};

export default router;
